import type { Form990Question } from '../types/Form990Types';

const form990Questions: Form990Question[] = [
  // Part III Questions
  {
    id: 'part3_q1',
    section: 'Part III',
    text: 'Did the organization discontinue its operations or dispose of more than 25% of its net assets?',
    tip: `This question asks about significant changes to your organization's operations or assets.

    Select "Yes" if either:
    • You completely stopped any major program or activity
    • You sold, transferred, or disposed of more than 25% of your total assets
    
    Examples of when to select "Yes":
    • Closed a major program that represented 30% of your activities
    • Sold a building worth 40% of your total assets
    • Transferred significant funds to another organization
    
    Don't Include:
    • Regular program adjustments
    • Normal asset sales
    • Minor operational changes`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule N',
      condition: (response) => response === true
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part3_q1'],
    defaultValue: false
  },
  {
    id: 'part3_q2',
    section: 'Part III',
    text: 'How many voting members are on the governing body?',
    tip: `Enter the total number of people who can vote on your Board of Directors.

    Include:
    • All board members with voting rights as of the end of the tax year
    • Both officers and directors who can vote
    
    Don't Include:
    • Advisory board members without voting rights
    • Ex-officio members who can't vote
    • Board members who left before year-end`,
    type: 'number',
    dataSource: {
      type: 'database',
      path: 'governance.boardMembers.voting'
    },
    validation: (value) => {
      if (value === null || value === undefined) return 'This field is required';
      if (value < 0) return 'Number cannot be negative';
      return true;
    },
    pdfMapping: ['f990_part3_votingMembers']
  },
  {
    id: 'part3_q3',
    section: 'Part III',
    text: 'How many independent voting members are on the governing body?',
    tip: `Enter the number of voting board members who are "independent".

    A board member is independent if ALL are true:
    • Receives less than $10,000 from your organization
    • Didn't receive compensation as a founder/major donor
    • Has no family member receiving compensation from your organization
    • Has no business relationship with your organization
    
    Example: 
    If you have 7 voting board members:
    • 5 receive no compensation
    • 1 is your paid Executive Director
    • 1 owns a company that provides services to your organization
    Enter "5" (only the uncompensated members count as independent)`,
    type: 'number',
    dataSource: {
      type: 'database',
      path: 'governance.boardMembers.independent'
    },
    validation: (value) => {
      if (value === null || value === undefined) return 'This field is required';
      if (value < 0) return 'Number cannot be negative';
      return true;
    },
    pdfMapping: ['f990_part3_independentVotingMembers']
  },
  {
    id: 'part3_q4',
    section: 'Part III',
    text: 'What is the total number of individuals employed during the calendar year?',
    tip: `Enter the total number of employees who received a W-2 during the year.

    Include:
    • Full-time employees
    • Part-time employees
    • Seasonal employees
    • Anyone who received a W-2, even if they only worked briefly
    
    Don't Include:
    • Independent contractors (1099 workers)
    • Volunteers
    • Board members (unless they're also employees)
    
    Example: If during the year you had:
    • 3 full-time staff
    • 2 part-time staff
    • 1 summer intern
    Enter "6" (total of all W-2 employees)`,
    type: 'number',
    dataSource: {
      type: 'database',
      path: 'employees.total'
    },
    validation: (value) => {
      if (value === null || value === undefined) return 'This field is required';
      if (value < 0) return 'Number cannot be negative';
      return true;
    },
    pdfMapping: ['f990_part3_totalEmployees']
  },
  {
    id: 'part3_q5',
    section: 'Part III',
    text: 'What is the estimated number of volunteers?',
    tip: `Provide your best estimate of how many volunteers helped during the year.

    Include:
    • Regular volunteers
    • One-time event volunteers
    • Board members who volunteer (beyond board service)
    • Committee members
    
    Example Scenarios:
    • 10 regular weekly volunteers
    • 25 people who helped at your annual fundraiser
    • 8 board members who also volunteer
    • 5 committee volunteers
    Total = 48 volunteers
    
    Note: It's okay to estimate. If someone volunteered multiple times, count them only once.
    Enter "0" if you had no volunteers.`,
    type: 'number',
    dataSource: {
      type: 'database',
      path: 'volunteers.total'
    },
    validation: (value) => {
      if (value === null || value === undefined) return 'This field is required';
      if (value < 0) return 'Number cannot be negative';
      return true;
    },
    pdfMapping: ['f990_part3_totalVolunteers']
  },
  {
    id: 'part3_q6',
    section: 'Part III',
    text: 'Did the organization undertake significant new program services during the year not listed on the prior Form 990?',
    tip: `This question asks about brand new programs or services your organization started this year.

    Select "Yes" if you:
    • Started any completely new programs
    • Launched major new services
    • Began serving a new population or area
    
    Examples of when to select "Yes":
    • Started a new after-school program
    • Launched an emergency relief fund
    • Added a new location or branch
    • Created a new scholarship program
    
    Don't Include:
    • Minor changes to existing programs
    • Normal growth of current services
    • One-time events within existing programs
    
    Note: If you select "Yes," you'll need to describe these new programs in Schedule O.`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule O',
      condition: (response) => response === true
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part3_newPrograms'],
    defaultValue: false
  },
  {
    id: 'part3_q7',
    section: 'Part III',
    text: 'Did the organization cease or make significant changes in how it conducts any program services?',
    tip: `This question asks about major changes to your existing programs.

    Select "Yes" if you:
    • Stopped any existing programs
    • Significantly changed how you deliver services
    • Substantially reduced or expanded a program
    
    Examples of when to select "Yes":
    • Switched from in-person to virtual services
    • Reduced service area by 50%
    • Changed from direct service to grant-making
    • Merged programs or restructured services
    
    Don't Include:
    • Minor adjustments to programs
    • Temporary changes due to circumstances
    • Normal program evolution
    
    Note: If you select "Yes," you'll need to explain these changes in Schedule O.`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule O',
      condition: (response) => response === true
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part3_programChanges'],
    defaultValue: false
  },
  {
    id: 'part3_q8',
    section: 'Part III',
    text: 'Describe the organization\'s top three program service accomplishments for the year.',
    tip: `Share your organization's biggest achievements and impact this year.

    For each program, include:
    • Brief description of the program
    • Number of people served
    • Total expenses for the program
    • Any revenue generated
    • Specific measurable outcomes
    
    Example Format:
    "Youth Education Program:
    • Served 500 students in after-school tutoring
    • 90% improved test scores by at least one grade level
    • Total expenses: $150,000
    • Program fees collected: $25,000"
    
    Tips:
    • Focus on impact and results
    • Use specific numbers when possible
    • List programs in order of expenses/importance
    • Include both direct and indirect beneficiaries`,
    type: 'multiline',
    dataSource: {
      type: 'database',
      path: 'programs.accomplishments'
    },
    pdfMapping: ['f990_part3_accomplishments']
  },
  // Part IV Questions
  {
    id: 'part4_q1',
    section: 'Part IV',
    text: 'Is the organization described under section 501(c)(3) or 4947(a)(1)?',
    tip: `This question determines if you're a public charity or private foundation.

    Select "Yes" if you are:
    • A 501(c)(3) public charity
    • A 501(c)(3) private foundation
    • A 4947(a)(1) charitable trust
    
    Common Examples:
    • Educational institutions
    • Religious organizations
    • Charitable organizations
    • Scientific organizations
    • Literary organizations
    
    Note: Most nonprofits using this system will select "Yes". If you're unsure, check your IRS determination letter or ask your tax advisor.
    
    If you select "Yes," Schedule A is required to determine your public charity status.`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule A',
      condition: (response) => response === true
    }],
    dataSource: {
      type: 'database',
      path: 'organization.taxStatus'
    },
    pdfMapping: ['f990_part4_501c3Status'],
    defaultValue: true
  },
  {
    id: 'part4_q2',
    section: 'Part IV',
    text: 'Did the organization receive any contributions that require Schedule B reporting?',
    tip: `Schedule B is required for specific contribution thresholds.

    Include:
    • Any contributor who gave $5,000 or more during the year
    • For religious organizations: Any contributor who gave 2% or more of total contributions
    • Multiple donations from one donor that total $5,000+
    
    Required to Report:
    • Money contributions
    • Property donations
    • In-kind services
    • Anything of value
    
    Don't Include:
    • Government grants
    • Public support from broad base of small donors
    • Program service revenue`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule B',
      condition: (response) => response === true
    }],
    dataSource: {
      type: 'calculated',
      calculation: (org) => org.contributions.any > 5000
    },
    pdfMapping: ['f990_part4_scheduleB'],
    defaultValue: false
  },
  {
    id: 'part4_q3',
    section: 'Part IV',
    text: 'Did the organization engage in direct or indirect political campaign activities?',
    tip: `This question asks about involvement in political campaigns.

    Reportable Activities:
    • Endorsing or opposing candidates
    • Donating to political campaigns
    • Hosting campaign events
    • Publishing materials favoring/opposing candidates
    
    Allowed Activities:
    • Nonpartisan voter registration
    • Neutral candidate forums
    • Issue advocacy not tied to candidates
    
    Prohibited Activities:
    • Direct campaign contributions
    • In-kind support to candidates
    • Partisan voter guides
    • Campaign fundraising`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule C',
      part: 'Part I',
      condition: (response) => response === true
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part4_politicalActivities'],
    defaultValue: false
  },
  {
    id: 'part4_q4',
    section: 'Part IV',
    text: 'Did the organization engage in lobbying activities or have a section 501(h) election in effect?',
    tip: `This question asks about attempts to influence legislation.

    Select "Yes" if your organization:
    • Contacted legislators about pending laws
    • Urged the public to contact legislators
    • Published materials about legislation
    • Made a 501(h) election for lobbying
    
    Examples of Lobbying:
    • Meeting with lawmakers about proposed bills
    • Running ads supporting/opposing legislation
    • Sending action alerts about pending votes
    • Testifying at legislative hearings
    
    Don't Include:
    • Nonpartisan analysis of legislation
    • Responses to legislative requests for testimony
    • Technical advice if invited by government
    
    Note: Limited lobbying is allowed for 501(c)(3)s, but must be reported.`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule C',
      part: 'Part II',
      condition: (response) => response === true
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part4_lobbyingActivities'],
    defaultValue: false
  },
  // Part V Questions
  {
    id: 'part5_q1',
    section: 'Part V',
    text: 'Did your organization pay any contractors or vendors $600 or more during the year?',
    tip: `This question helps determine if you filed the required Form 1096 with the IRS.

    Form 1096 summarizes all 1099 forms you sent to contractors/vendors. You need this if you:
    • Paid any individual/company $600+ for services (not goods)
    • Example: Paid a consultant $1,000 for strategic planning
    • Example: Paid a contractor $750 for website design
    
    The system will automatically calculate how many 1096 forms were filed based on your vendor payment records.`,
    type: 'boolean',
    dataSource: {
      type: 'database',
      path: 'taxForms.hasContractorPayments'
    },
    validation: (value) => {
      if (value === null || value === undefined) return 'Please select Yes or No';
      return true;
    },
    pdfMapping: ['f990_part5_form1096'],
    defaultValue: false
  },
  {
    id: 'part5_q3',
    section: 'Part V',
    text: 'Did the organization follow IRS backup withholding rules?',
    tip: `Backup withholding is when you must withhold 24% of payments to vendors/contractors who:
    • Did not provide a valid Tax ID (EIN or SSN)
    • Received a notice from the IRS requiring backup withholding
    
    Example: If you paid a contractor $1,000 but they wouldn't provide their Tax ID:
    • You must withhold $240 (24%) and send it to the IRS
    • Pay the contractor the remaining $760
    
    Select "Yes" if either:
    • You properly withheld when required, OR
    • You were never required to withhold (most common)
    
    Select "No" if you should have withheld but didn't.`,
    type: 'boolean',
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part5_backupWithholding'],
    defaultValue: true
  },
  // Part VI Questions
  {
    id: 'part6_q1',
    section: 'Part VI',
    text: 'Did the organization have any changes to its governing documents during the year?',
    tip: `This question asks about changes to your official organizational documents.

    Select "Yes" if you modified:
    • Articles of Incorporation
    • Bylaws
    • Constitution
    • Other governing documents
    
    Reportable Changes:
    • Changed board structure or term limits
    • Modified mission statement
    • Updated voting requirements
    • Added/removed officer positions
    
    Don't Include:
    • Policy changes not in governing documents
    • Routine updates to procedures
    • Changes to non-governing documents
    
    Note: If you select "Yes," you'll need to describe these changes in Schedule O.`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule O',
      condition: (response) => response === true
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part6_governingDocs'],
    defaultValue: false
  },
  {
    id: 'part6_q2',
    section: 'Part VI',
    text: 'Did the organization make its governing documents available to the public?',
    tip: `This question asks about public access to your organizational documents.

    Select "Yes" if you make available:
    • Articles of Incorporation
    • Bylaws
    • Form 990
    • Conflict of Interest Policy
    
    Common Methods of Making Documents Available:
    • Posted on your website
    • Available upon request
    • Listed on GuideStar/Charity Navigator
    • Kept at main office for inspection
    
    Best Practices:
    • Have a written policy for handling requests
    • Maintain current versions readily available
    • Redact private information before sharing
    • Track document requests
    
    Don't Include:
    • Private donor information
    • Confidential personnel matters
    • Internal working documents`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule O',
      condition: (response) => response === false
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part6_publicDocs'],
    defaultValue: true
  },
  {
    id: 'part6_q3',
    section: 'Part VI',
    text: 'Does the organization have a written conflict of interest policy?',
    tip: `This question asks about your formal policy for handling conflicts of interest.

    Select "Yes" if you have:
    • Written conflict of interest policy
    • Annual disclosure requirements
    • Documented monitoring process
    • Enforcement procedures
    
    Required Elements:
    • Definition of conflicts
    • Disclosure procedures
    • Review process
    • Documentation requirements
    
    Examples of Conflicts:
    • Board member's company provides services
    • Family member hired as employee
    • Officer also serves on vendor's board
    • Director receives grant funding
    
    Don't Include:
    • Informal understandings
    • Unwritten policies
    • Outdated policies
    • Unenforced policies`,
    
    type: 'boolean',
    dataSource: {
      type: 'database',
      path: 'governance.policies.conflictOfInterest'
    },
    pdfMapping: ['f990_part6_conflictPolicy'],
    defaultValue: true
  },
  {
    id: 'part7_q1',
    section: 'Part VII',
    text: 'Did the organization compensate any current or former officers, directors, or key employees?',
    tip: `This question asks about compensation to organization leaders.

    Select "Yes" if you:
    • Paid salaries to officers or directors
    • Provided benefits to key employees
    • Compensated former leaders
    • Issued stipends or honoraria
    
    Required to Report:
    • All forms of compensation
    • Health benefits
    • Retirement contributions
    • Expense allowances
    
    Don't Include:
    • Volunteer board service
    • Reimbursement for actual expenses
    • De minimis benefits
    • Non-compensated positions`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule J',
      condition: (response) => response === true
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part7_q1'],
    defaultValue: false
  },
  {
    id: 'part8_q1',
    section: 'Part VIII',
    text: 'Did the organization receive contributions of art, historical treasures, or similar assets?',
    tip: `This question asks about non-cash contributions of significant items.

    Select "Yes" if you received:
    • Artwork or antiques
    • Historical artifacts
    • Scientific specimens
    • Archival materials
    
    Required Documentation:
    • Written acknowledgments
    • Appraisals when required
    • Donor restrictions
    • Usage agreements
    
    Don't Include:
    • Standard office equipment
    • Ordinary supplies
    • Used clothing or furniture
    • Items for immediate resale`,
    type: 'boolean',
    requiresSchedule: [{
      name: 'Schedule M',
      condition: (response) => response === true
    }],
    dataSource: { type: 'manual' },
    pdfMapping: ['f990_part8_q1'],
    defaultValue: false
  }
];

// Let's log out all question IDs to see exactly what we have
console.log('Form 990 Question IDs:', form990Questions.map(q => q.id));

export const getRequiredSchedules = (responses: Record<string, any>): string[] => {
  const requiredSchedules = new Set<string>();
  
  form990Questions.forEach(question => {
    if (question.requiresSchedule) {
      question.requiresSchedule.forEach(schedule => {
        if (!schedule.condition || schedule.condition(responses[question.id])) {
          requiredSchedules.add(schedule.name);
        }
      });
    }
  });

  return Array.from(requiredSchedules);
};

export const getInitialResponses = (questions: Form990Question[]): Record<string, any> => {
  return questions.reduce((acc, question) => {
    if ('defaultValue' in question) {
      acc[question.id] = question.defaultValue;
    }
    return acc;
  }, {} as Record<string, any>);
};

export {
  form990Questions,
  // any other exports needed
};